class ConductricsABTestHideButton {
  /**
   * Creates an instance of ConductricsABTestHideButton.
   * @param {string} buttonToHide - The name of the button to be hidden during the A/B test.
   * @param {boolean} devMode - Flag to enable or disable development mode logging.
   */
  constructor(buttonToHide, devMode) {
    this.buttonToHide = buttonToHide; // Button to hide
    this.devMode = devMode; // Development mode flag
    this.maxCheckTime = 5000; // Maximum time to check for button presence in milliseconds
    this.checkIntervalTime = 100; // Interval time in milliseconds for checking the button
    this.intervalRunning = false; // Flag to track if the interval is currently running

    // Setting up event listeners
    document.addEventListener('DOMContentLoaded', () => this.run()); // Run the test once the DOM is fully loaded
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.run()); // Run the test on cart updates

    // Initialize the MutationObserver to watch for changes in the cart drawer
    const cartDrawer = document.querySelector('cart-drawer');
    this.initializeMutationObserver(cartDrawer);
    this.run(); // Call run() when an instance is initialized
  }

  /**
   * Logs an error message to the console.
   * @param {string} message - Error message to log.
   * @param {Error} error - Optional error object for additional context.
   */
  logError(message, error) {
    console.error(message, error);
  }

  /**
   * Logs information to the console if in development mode.
   * @param {string} message - Message to log.
   * @param {any} data - Optional data to log alongside the message.
   */
  logInfo(message, data, force = false) {
    if (this.devMode || force) {
      console.log(`%c${message}`, 'color: green; font-weight: bold;'); // Log message in green bold text
      if (data) {
        console.log(data); // Log additional data if provided
      }
    }
  }

  /**
   * Attempts to remove the specified express button from the DOM.
   * @returns {boolean} - Returns true if the button was successfully removed, false otherwise.
   */
  removeExpressButton() {
    try {
      const shadowRoot = document.querySelector('shopify-accelerated-checkout-cart')?.getOrCreateShadowRoot() ?? null;
      const wrapper = shadowRoot?.querySelector('div') ?? null; // Get the shadow root wrapper

      if (!wrapper) {
        return this.logInfo('Wrapper element not found.');
      }

      const expressButton = wrapper.querySelector(`.wallet-cart-button-container:has([name="${this.buttonToHide}"])`);
      if (expressButton) {
        expressButton.style.display = 'none'; // Hide the button if found
        // Overwrite CSS variable --wallet-button-width-horizontal
        shadowRoot.querySelector('style').textContent += `
        .wallet-cart-wrapper {
         --wallet-grid-margin-horizontal: 0px;
        }
        .wallet-cart-button-container {
            --wallet-button-width-horizontal: 100%;
            margin: 0;
        }`;
        this.logInfo(
          `Express Checkout AB Test => Successfully removed express checkout button: ${this.buttonToHide}`,
          null,
          true
        );
        return true;
      }
      this.logInfo('Express button not found in the wrapper.');
      return false;
    } catch (error) {
      this.logError('Error in removeExpressButton:', error);
      return false;
    }
  }

  /**
   * Monitors the availability of the target method and manages the check interval.
   * @param {Element} targetNode - The target node to monitor for the express button.
   */
  monitorMethodAvailability(targetNode) {
    this.intervalRunning = true; // Set flag to indicate interval is running
    let timer = 0; // Initialize timer

    const checkInterval = setInterval(() => {
      timer += this.checkIntervalTime; // Increment timer

      if (typeof targetNode.getOrCreateShadowRoot === 'function') {
        if (this.removeExpressButton() || timer >= this.maxCheckTime) {
          this.clearInterval(checkInterval); // Clear interval if button is removed or max time reached
        }
      } else if (timer >= this.maxCheckTime) {
        this.logError(`getOrCreateShadowRoot is not available on the targetNode after ${timer / 1000} seconds`);
        this.clearInterval(checkInterval); // Clear interval if target method is not available
      }
    }, this.checkIntervalTime);
  }

  /**
   * Clears the specified interval and resets the running flag.
   * @param {number} checkInterval - The interval ID to clear.
   */
  clearInterval(checkInterval) {
    clearInterval(checkInterval); // Clear the interval
    this.intervalRunning = false; // Reset the running flag
    this.logInfo('Interval finished.'); // Log that the interval has finished
  }

  /**
   * Runs the main logic of the A/B test.
   */
  run() {
    if (this.intervalRunning) return; // Do not run the test if the interval is already running
    const targetNode = document.querySelector('shopify-accelerated-checkout-cart'); // Get the target node

    if (targetNode) {
      this.monitorMethodAvailability(targetNode); // Monitor the method availability if target node is valid
    } else {
      this.logError('Target node is not valid:', targetNode); // Log error if target node is invalid
    }
  }

  /**
   * Initializes a MutationObserver to watch for changes to the target element.
   * @param {Element} targetElement - The element to observe for mutations.
   */
  initializeMutationObserver(targetElement) {
    if (!targetElement) {
      this.logError('Target element not found.');
      return;
    }

    const observer = new MutationObserver((mutationsList) => {
      mutationsList.forEach((mutation) => {
        if (mutation.type === 'childList') {
          this.run(); // Call the run function whenever a child node is added or removed
        }
      });
    });

    const config = { childList: true, subtree: true };
    observer.observe(targetElement, config);
  }
}

/*
new ConductricsABTestHideButton('button-PayPal');
new ConductricsABTestHideButton('button-ShopPay');
 */
